import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { TimeRequestOptions, timeReportsAPI } from '../services/api';
import { TimeSummaryReport, TimeWeeklyReport } from '../types';
import { endOfMonthIso, formatDate, formatDurationMinutes, startOfMonthIso, todayIso } from '../utils';
import MonthPicker from './MonthPicker';

interface DailyHourGap {
  timeProfileId: number;
  timeProfileName: string;
  date: string;
  totalMinutes: number;
  missingMinutes: number;
}

interface DashboardWarning {
  id: string;
  period: 'Selected month';
  timeProfileId: number;
  timeProfileName: string;
  gaps: DailyHourGap[];
}

interface MonthlyProfileWorkload {
  timeProfileId: number;
  timeProfileName: string;
  totalMinutes: number;
  gaps: DailyHourGap[];
}

const emptySummary: TimeSummaryReport = {
  startDate: todayIso(),
  endDate: todayIso(),
  entryCount: 0,
  profileCount: 0,
  totalMinutes: 0,
  totalHours: 0,
  billableMinutes: 0,
  billableHours: 0,
  nonBillableMinutes: 0,
  nonBillableHours: 0,
  byProfile: [],
  byProject: [],
  byCustomer: [],
  byBillableStatus: [],
};

const toLocalDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDaysIso = (isoDate: string, days: number) => {
  const date = toLocalDate(isoDate);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
};

const monthToRange = (monthValue: string) => {
  const [year, month] = monthValue.split('-').map(Number);
  const monthDate = new Date(year, month - 1, 1);
  return {
    startDate: startOfMonthIso(monthDate),
    endDate: endOfMonthIso(monthDate),
  };
};

const isWorkday = (isoDate: string) => {
  const day = toLocalDate(isoDate).getDay();
  return day >= 1 && day <= 5;
};

const countWorkdays = (startDate: string, endDate: string) => {
  let total = 0;
  let cursor = startDate;

  while (cursor <= endDate) {
    if (isWorkday(cursor)) {
      total += 1;
    }
    cursor = addDaysIso(cursor, 1);
  }

  return total;
};

const buildMonthlyWorkloads = (reports: TimeWeeklyReport[], startDate: string, endDate: string): MonthlyProfileWorkload[] => {
  const workloadsByProfile = new Map<number, MonthlyProfileWorkload>();

  reports.forEach(report => {
    report.profiles.forEach(profile => {
      if (profile.ignoreForWarnings) {
        return;
      }

      const workload = workloadsByProfile.get(profile.timeProfileId) ?? {
        timeProfileId: profile.timeProfileId,
        timeProfileName: profile.timeProfileName,
        totalMinutes: 0,
        gaps: [],
      };

      profile.days.forEach(day => {
        const date = day.date.split('T')[0];
        if (date < startDate || date > endDate) {
          return;
        }

        workload.totalMinutes += day.totalMinutes;

        if (isWorkday(date) && day.totalMinutes < 480) {
          workload.gaps.push({
            timeProfileId: profile.timeProfileId,
            timeProfileName: profile.timeProfileName,
            date,
            totalMinutes: day.totalMinutes,
            missingMinutes: 480 - day.totalMinutes,
          });
        }
      });

      workloadsByProfile.set(profile.timeProfileId, workload);
    });
  });

  return Array.from(workloadsByProfile.values()).map(workload => ({
    ...workload,
    gaps: workload.gaps.sort((left, right) =>
      left.date.localeCompare(right.date) ||
      right.missingMinutes - left.missingMinutes ||
      left.timeProfileName.localeCompare(right.timeProfileName)
    ),
  }));
};

const buildDashboardWarnings = (
  workloads: MonthlyProfileWorkload[],
  expectedMonthlyMinutes: number
): DashboardWarning[] => {
  return workloads
    .filter(workload => workload.totalMinutes < expectedMonthlyMinutes && workload.gaps.length > 0)
    .map<DashboardWarning>(workload => ({
      id: `month-${workload.timeProfileId}`,
      period: 'Selected month',
      timeProfileId: workload.timeProfileId,
      timeProfileName: workload.timeProfileName,
      gaps: workload.gaps,
    }))
    .sort((left, right) => {
      const leftFirstGap = left.gaps[0];
      const rightFirstGap = right.gaps[0];

      return leftFirstGap.date.localeCompare(rightFirstGap.date) ||
        rightFirstGap.missingMinutes - leftFirstGap.missingMinutes ||
        left.timeProfileName.localeCompare(right.timeProfileName);
    });
};

const DailyGapRow: React.FC<{ warning: DashboardWarning; period?: string }> = ({ warning, period }) => {
  const [isExpanded, setExpanded] = useState(false);
  const detailsId = useId();
  const totalMissingMinutes = warning.gaps.reduce((sum, gap) => sum + gap.missingMinutes, 0);
  const actionLabel = `${isExpanded ? 'Hide' : 'Show'} ${warning.timeProfileName} missing-hours details`;

  return (
    <div className="daily-gap-row">
      <div className="daily-gap-summary">
        <strong>{warning.timeProfileName}</strong>
        <span>{period ? `${period}: ` : ''}{warning.gaps.length} workday{warning.gaps.length === 1 ? '' : 's'} below 8h</span>
        <span>{formatDurationMinutes(totalMissingMinutes)} missing total</span>
        {isExpanded && (
          <ul className="daily-gap-days" id={detailsId}>
            {warning.gaps.map(gap => (
              <li key={gap.date}>
                <span>{formatDate(gap.date)} below 8h</span>
                <strong>{formatDurationMinutes(gap.missingMinutes)} missing</strong>
                <span>{formatDurationMinutes(gap.totalMinutes)} logged</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        className="btn btn-secondary btn-sm daily-gap-toggle"
        type="button"
        aria-controls={detailsId}
        aria-expanded={isExpanded}
        aria-label={actionLabel}
        onClick={() => setExpanded(current => !current)}
      >
        {isExpanded ? 'Hide details' : 'Show details'}
      </button>
    </div>
  );
};

const DailyGapList: React.FC<{ warnings: DashboardWarning[]; loading: boolean }> = ({ warnings, loading }) => {
  if (loading) {
    return <div className="muted-row">Loading...</div>;
  }

  if (warnings.length === 0) {
    return <div className="empty-state">All members have 8h per workday</div>;
  }

  return (
    <div className="daily-gap-list">
      {warnings.map(warning => (
        <DailyGapRow warning={warning} key={warning.id} />
      ))}
    </div>
  );
};

const WarningPreviewList: React.FC<{ warnings: DashboardWarning[]; loading: boolean }> = ({ warnings, loading }) => {
  if (loading) {
    return <div className="muted-row">Loading...</div>;
  }

  if (warnings.length === 0) {
    return <div className="empty-state">All members have 8h per workday</div>;
  }

  return (
    <div className="daily-gap-list">
      {warnings.slice(0, 3).map(warning => (
        <DailyGapRow warning={warning} period={warning.period} key={warning.id} />
      ))}
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const { actualIsAdmin } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(() => todayIso().slice(0, 7));
  const selectedMonthRange = useMemo(() => monthToRange(selectedMonth), [selectedMonth]);
  const [summary, setSummary] = useState<TimeSummaryReport>(() => ({
    ...emptySummary,
    startDate: selectedMonthRange.startDate,
    endDate: selectedMonthRange.endDate,
  }));
  const [monthlyWeekly, setMonthlyWeekly] = useState<TimeWeeklyReport[]>([]);
  const [isWarningsOpen, setWarningsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setMonthlyLoading(true);
    setError('');

    const adminViewOptions: TimeRequestOptions = { viewMode: 'Admin' };
    const filter = {
      startDate: selectedMonthRange.startDate,
      endDate: selectedMonthRange.endDate,
    };

    try {
      const [summaryData, monthlyWeeklyData] = await Promise.all([
        timeReportsAPI.getSummary(filter, adminViewOptions),
        timeReportsAPI.getWeekly(filter, adminViewOptions),
      ]);
      setSummary(summaryData);
      setMonthlyWeekly(monthlyWeeklyData);
    } catch {
      setError('Admin dashboard data could not be loaded');
      toast.error('Monthly dashboard data could not be loaded');
    } finally {
      setLoading(false);
      setMonthlyLoading(false);
    }
  }, [selectedMonthRange.endDate, selectedMonthRange.startDate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const currentDate = todayIso();
  const selectedMonthWarningEndDate = selectedMonthRange.endDate >= currentDate
    ? addDaysIso(currentDate, -1)
    : selectedMonthRange.endDate;

  const expectedMonthlyMinutes = useMemo(() => {
    return countWorkdays(selectedMonthRange.startDate, selectedMonthWarningEndDate) * 480;
  }, [selectedMonthRange.startDate, selectedMonthWarningEndDate]);

  const monthlyWorkloads = useMemo(() => {
    return buildMonthlyWorkloads(monthlyWeekly, selectedMonthRange.startDate, selectedMonthWarningEndDate);
  }, [monthlyWeekly, selectedMonthRange.startDate, selectedMonthWarningEndDate]);

  const dashboardWarnings = useMemo<DashboardWarning[]>(() => {
    return buildDashboardWarnings(monthlyWorkloads, expectedMonthlyMinutes);
  }, [expectedMonthlyMinutes, monthlyWorkloads]);

  if (!actualIsAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Admin dashboard</h1>
          <p>All members, {formatDate(summary.startDate)} - {formatDate(summary.endDate)}</p>
        </div>
        <div className="time-filter-field">
          <MonthPicker
            id="admin-dashboard-month"
            label="Dashboard month"
            value={selectedMonth}
            onChange={setSelectedMonth}
          />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="stat-grid">
        <div className="stat-card">
          <span>Total</span>
          <strong>{formatDurationMinutes(summary.totalMinutes)}</strong>
        </div>
        <div className="stat-card">
          <span>Billable</span>
          <strong>{formatDurationMinutes(summary.billableMinutes)}</strong>
        </div>
        <div className="stat-card">
          <span>Members</span>
          <strong>{summary.profileCount}</strong>
        </div>
        <div className="stat-card">
          <span>Warnings</span>
          <strong>{dashboardWarnings.length}</strong>
        </div>
      </div>

      {isWarningsOpen && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setWarningsOpen(false);
            }
          }}
        >
          <section className="time-modal time-modal-wide" role="dialog" aria-modal="true" aria-labelledby="warnings-dialog-title">
            <div className="time-modal-header">
              <h5 id="warnings-dialog-title" className="card-title">All warnings</h5>
              <button className="icon-button" type="button" onClick={() => setWarningsOpen(false)} aria-label="Close warnings">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <div className="daily-gap-section">
                <h6>Selected month</h6>
                <DailyGapList warnings={dashboardWarnings} loading={monthlyLoading} />
              </div>
            </div>
          </section>
        </div>
      )}

      <div className="time-grid two-col">
        <section className="card">
          <div className="card-header daily-gap-card-header">
            <h5 className="card-title">Missing daily hours</h5>
            {dashboardWarnings.length > 3 && (
              <button className="btn btn-secondary btn-sm" type="button" onClick={() => setWarningsOpen(true)}>
                View all
              </button>
            )}
          </div>
          <div className="card-body">
            <WarningPreviewList warnings={dashboardWarnings} loading={monthlyLoading} />
          </div>
        </section>

        <section className="card">
          <div className="card-header">
            <h5 className="card-title">Top projects</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="muted-row">Loading...</div>
            ) : summary.byProject.length === 0 ? (
              <div className="empty-state">No entries</div>
            ) : (
              <div className="summary-list">
                {summary.byProject.slice(0, 5).map(item => (
                  <div className="summary-row" key={item.id || item.key}>
                    <span>{item.key}</span>
                    <strong>{formatDurationMinutes(item.totalMinutes)}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
